import { StoreModel } from './index'
import rollbar from '@mortgage-pos/ui/services/rollbar'
import newRelic from '@mortgage-pos/ui/services/newRelic'
import {
  readSageInternalCookie,
  recursivelyRemoveEmptyValues,
  setCookie,
} from '@mortgage-pos/utils'
import { borrowerPortalRequest } from '@mortgage-pos/ui/services/http'
import incense from '@mortgage-pos/ui/incense'

import {
  BankrateAnswers,
  GetAnswersResult,
  QuestionnaireType,
  ApplicationAnswersOutput,
  LoanPurpose,
  DirectMailerAnswers,
  CookieName,
} from '@mortgage-pos/types'

import {
  thunk,
  Thunk,
  action,
  Action,
  thunkOn,
  ThunkOn,
  computed,
  Computed,
} from 'easy-peasy'

import { FEATURE_FLAG_TYPES } from '@mortgage-pos/data'
import { variation } from '@mortgage-pos/ui/services/featureFlags'
import { calculateMaxCashout } from '../utils/maxCashout'
import ApplicationClient from '../services/ApplicationClient'
import { LoanMatchDetailsObject } from '@mortgage-pos/db'

// Helper function to map numeric credit scores to UI option values
// Contentful option values are: 780, 770, 750, 730, 710, 690, 670, 650, 630, 600
// We need to map the numeric credit score to the UI option value
// Must be a string because the UI option values are strings
function mapNumericCreditScoreToRange(creditScore: number): string | null {
  if (!creditScore) return null

  switch (true) {
    case creditScore >= 780:
      return '780'
    case creditScore >= 760:
      return '770'
    case creditScore >= 740:
      return '750'
    case creditScore >= 720:
      return '730'
    case creditScore >= 700:
      return '710'
    case creditScore >= 680:
      return '690'
    case creditScore >= 660:
      return '670'
    case creditScore >= 640:
      return '650'
    case creditScore >= 620:
      return '630'
    case creditScore >= 580:
      return '600'
    default:
      return '600' // Default to lowest option
  }
}

export interface LeadAnswersPayload extends GetAnswersResult {
  skippedHydration?: boolean
}

export interface AnswersModel {
  // State
  answers: any
  applicationAnswers: ApplicationAnswersOutput
  bankrateAnswers: BankrateAnswers
  directMailerAnswers: DirectMailerAnswers
  loanMatchAnswers: LoanMatchDetailsObject
  isPrefillingAnswers: boolean
  showSsnSuccess: boolean

  // Computed
  mergedAnswers: Computed<AnswersModel, any, StoreModel>
  maxCashoutValue: Computed<AnswersModel, any, StoreModel>

  // Actions
  setAnswers: Action<AnswersModel, object>
  setIsPrefillingAnswers: Action<AnswersModel, boolean>
  setApplicationAnswers: Action<AnswersModel, ApplicationAnswersOutput>
  setLoanMatchAnswers: Action<AnswersModel, LoanMatchDetailsObject>
  setBankrateAnswers: Action<AnswersModel, BankrateAnswers>
  setDirectMailerAnswers: Action<AnswersModel, DirectMailerAnswers>
  setShowSsnSuccess: Action<AnswersModel, boolean>

  // Thunks
  setLeadAnswers: Thunk<AnswersModel, object, object, StoreModel>
  getAuthenticatedAnswers: ThunkOn<AnswersModel, object, StoreModel>

  /** TEST UTILITIES **/
  resetAnswers: Action<AnswersModel>
  resetApplicationAnswers: Action<AnswersModel>
  resetBankrateAnswers: Action<AnswersModel>
}

const answers = (): AnswersModel => {
  return {
    answers: {},

    applicationAnswers: {},

    bankrateAnswers: {},

    directMailerAnswers: {},

    loanMatchAnswers: {},

    isPrefillingAnswers: null,

    showSsnSuccess: false,

    mergedAnswers: computed((state: AnswersModel) => {
      return {
        ...state.bankrateAnswers,
        ...state.applicationAnswers,
        ...state.directMailerAnswers,
        ...state.answers,
      }
    }),

    maxCashoutValue: computed(
      [(state) => state.mergedAnswers],
      (mergedAnswers) => {
        const { estimatedValue, remainingBalance, propertyType, propertyUse } =
          mergedAnswers
        return calculateMaxCashout({
          estimatedValue,
          remainingBalance,
          propertyType,
          propertyUse,
        })
      }
    ),

    setIsPrefillingAnswers: action((state, payload) => {
      state.isPrefillingAnswers = payload
    }),

    setAnswers: action((state, payload: any) => {
      // Map numeric credit score to range string for UI display
      if (payload.creditScore) {
        if (typeof payload.creditScore === 'number') {
          const mapped = mapNumericCreditScoreToRange(payload.creditScore)
          payload.creditScore = mapped
        }
      }
      state.answers = { ...state.answers, ...payload }
    }),

    setLeadAnswers: thunk(
      (
        {
          setBankrateAnswers,
          setApplicationAnswers,
          setAnswers,
          setDirectMailerAnswers,
        },
        { skippedHydration, ...leadAnswers }: LeadAnswersPayload,
        { getState, getStoreActions, getStoreState }
      ) => {
        const { answers } = getState()
        const { questionnaireType, slug, isMiniFlow } =
          getStoreState().questionnaire
        const { tracking } = getStoreActions()
        const { setApplicationId, setCoBorrowerId } =
          getStoreActions().application
        const initialAnswers: any = {}

        if (leadAnswers.bankrateAnswers) {
          const bankrateAnswers = leadAnswers.bankrateAnswers

          // Rely on AVM for estimated value instead
          if (questionnaireType === QuestionnaireType.NTL) {
            delete bankrateAnswers.estimatedValue
          }

          setBankrateAnswers(bankrateAnswers)
          initialAnswers.createAccount = [{ email: bankrateAnswers.email }]
          initialAnswers.declarationMilitary =
            bankrateAnswers.declarationMilitary
          if (!readSageInternalCookie()) {
            tracking.identify()
          }
        }

        if (leadAnswers.applicationAnswers) {
          const applicationAnswers = recursivelyRemoveEmptyValues(
            leadAnswers.applicationAnswers
          )

          if (applicationAnswers.applicationId) {
            setApplicationId(applicationAnswers.applicationId)
          }

          // Prefilled apps with an existing co-borrower carry coBorrowerId here;
          // the save pipeline reads it from the application store for co-borrower
          // mutations, so mirror it over or those saves target a null/wrong record.
          if (applicationAnswers.coBorrowerId) {
            setCoBorrowerId(applicationAnswers.coBorrowerId)
          }

          if (applicationAnswers?.email) {
            initialAnswers.createAccount = [{ email: applicationAnswers.email }]
          }

          setApplicationAnswers(applicationAnswers)
        }

        if (leadAnswers.directMailerAnswers) {
          const directMailerAnswers = leadAnswers.directMailerAnswers
          setDirectMailerAnswers(directMailerAnswers)
        }

        if (!answers.loanPurpose) {
          const prefilledLoanPurpose =
            leadAnswers.applicationAnswers?.loanPurpose ||
            leadAnswers.bankrateAnswers?.loanPurpose

          // When hydration was skipped on purpose (phone flow), a slug guess would be
          // saved over the purpose the LO already stored.
          if (prefilledLoanPurpose || !skippedHydration) {
            initialAnswers.loanPurpose =
              prefilledLoanPurpose || getDefaultLoanPurpose(slug)
          }

          // A new funnel entry legitimately has no purpose yet, so only report when
          // an already-started application came back without one.
          if (!prefilledLoanPurpose && leadAnswers.applicationAnswers) {
            const { applicationId, applicationGuid } =
              getStoreState().application

            reportUnprefilledLoanPurpose({
              applicationId,
              applicationGuid,
              isAuthenticated: getStoreState().authentication.isAuthenticated,
              slug,
              isMiniFlow,
              guessedLoanPurpose: initialAnswers.loanPurpose,
            })
          }
        }

        if (Object.keys(initialAnswers).length) {
          setAnswers(initialAnswers)
        }
      }
    ),

    setApplicationAnswers: action((state, applicationAnswers) => {
      state.applicationAnswers = applicationAnswers
    }),

    setLoanMatchAnswers: action((state, loanMatchAnswers) => {
      state.loanMatchAnswers = loanMatchAnswers
    }),

    setBankrateAnswers: action((state, bankrateAnswers) => {
      const { id, ...data } = bankrateAnswers
      state.bankrateAnswers = data
    }),

    setDirectMailerAnswers: action((state, directMailerAnswers) => {
      state.directMailerAnswers = directMailerAnswers
    }),

    setShowSsnSuccess: action((state, showSsnSuccess) => {
      state.showSsnSuccess = showSsnSuccess
    }),

    getAuthenticatedAnswers: thunkOn(
      (_, storeActions) => storeActions.authentication.setIsAuthenticated,
      async (
        actions,
        setAuthedListener,
        { getState, getStoreState, getStoreActions }
      ) => {
        if (setAuthedListener.payload !== true) {
          actions.setIsPrefillingAnswers(false)
          return
        }

        const { isPhoneFlow, isLoanMatchFlow } = getStoreState().questionnaire
        const isPhoneFlowEnabled = await variation(
          FEATURE_FLAG_TYPES.PHONE_FLOW_ENABLED
        )
        const isPhoneApp = isPhoneFlow && isPhoneFlowEnabled

        // Don't make prefill call for first time users after account creation when setIsAuthenticated is called
        // Unless it's a phone app or from Bankrate Pro
        if (
          getStoreState().pageSections.pageSectionsList.includes(
            'Create Account'
          ) &&
          !isPhoneApp
        ) {
          actions.setIsPrefillingAnswers(false)
          return
        }

        actions.setIsPrefillingAnswers(true)

        const urlParams = new URLSearchParams(window.location.search)
        const passedAppGuid = urlParams.get('application-guid')

        let response
        try {
          response = await borrowerPortalRequest(
            '/prefill',
            passedAppGuid ? { applicationGuid: passedAppGuid } : {}
          )
        } catch (e) {
          rollbar.error('Failed to prefill answers for authenticated user', e)
          newRelic.increment('application_answers_fetch.failure')

          actions.setIsPrefillingAnswers(false)

          return
        }

        const {
          applicationId,
          applicationGuid,
          coBorrowerId,
          applicationCreatedAt,
          auth0Id,
        } = response.data
        const {
          setApplicationId,
          setApplicationGuid,
          setCoBorrowerId,
          setApplicationCreatedAt,
          hydrateLeadSource,
        } = getStoreActions().application

        if (applicationId) {
          setApplicationId(applicationId)
        }

        if (applicationGuid) {
          // update/set the applicationId & appGuid in the session since
          // we wont have called ApplicationClient.initialize() on the
          // authenticated user route
          setApplicationGuid(applicationGuid)
          await ApplicationClient.updateApplicationSession(applicationGuid)
          await hydrateLeadSource()
        }

        if (coBorrowerId) {
          setCoBorrowerId(coBorrowerId)
        }

        if (applicationCreatedAt) {
          setApplicationCreatedAt(new Date(applicationCreatedAt))
        }

        if (auth0Id) {
          setCookie(CookieName.UserId, auth0Id)
        }

        if (isLoanMatchFlow) {
          try {
            const loanMatchDetails = await borrowerPortalRequest(
              '/loan-match/details',
              {},
              /**
               * Development only headers
               *  { application_id_override: '1111' },
               */
              {},
              'get'
            )

            if (loanMatchDetails?.data?.pricing_inputs) {
              actions.setLoanMatchAnswers(loanMatchDetails.data.pricing_inputs)
            }
          } catch (error) {
            rollbar.error('Failed to prefill loan match answers', error)
            newRelic.increment('loan_match_answers_fetch.failure')
          }
        }

        actions.setAnswers(response.data)
        actions.setIsPrefillingAnswers(false)
        newRelic.increment('application_answers_fetch.success')
      }
    ),

    /** TEST UTILITIES **/

    resetAnswers: action((state) => {
      state.answers = {}
    }),

    resetApplicationAnswers: action((state) => {
      state.applicationAnswers = {}
    }),

    resetBankrateAnswers: action((state) => {
      state.bankrateAnswers = {}
    }),
  }
}

/**
 * The purpose below is guessed from the question set, so a later fill can persist
 * over the real one. Report it to pair with the server-side transition log.
 */
function reportUnprefilledLoanPurpose({
  applicationId,
  applicationGuid,
  isAuthenticated,
  slug,
  isMiniFlow,
  guessedLoanPurpose,
}: {
  applicationId: number
  applicationGuid: string
  isAuthenticated: boolean
  slug: string
  isMiniFlow: boolean
  guessedLoanPurpose: string
}) {
  incense()
    .details({
      name: 'UnprefilledLoanPurpose',
      message: `loanPurpose guessed as ${guessedLoanPurpose} from question set ${slug}`,
    })
    .safe({
      applicationId,
      applicationGuid,
      isAuthenticated,
      slug,
      isMiniFlow,
      guessedLoanPurpose,
    })
    .warn()
}

function getDefaultLoanPurpose(slug) {
  if (slug?.indexOf('purchase') > -1) {
    return LoanPurpose.Purchase
  }

  if (slug?.indexOf('home-equity') > -1) {
    return LoanPurpose.HomeEquity
  }

  return LoanPurpose.Refinance
}

export default answers
